import styled from "@emotion/styled";
import { Content, DSQuestion, DSQuestionTitle } from "@hundred5/design-system";
import { Trans } from "@lingui/react/macro";
import { Dispatch, useCallback, useEffect, useRef } from "react";
import { useMedia } from "react-use";

import { usePostTestCopyPasteEventsMutation } from "@/queries/tests/mutations";
import { useGetTest } from "@/queries/tests/queries";
import { PublicQuestion } from "@/queries/types";
import { useRouter } from "next/router";
import { withDisabledCopy } from "../../../utils/with-disabled-copy";
import { Markdown } from "../../markdown";
import { ActiveAnswer } from "../active-answer/active-answer";
import { FooterQuestionNavigation } from "../footer-question-navigation";
import { QuestionAttachments } from "../question-attachments";
import { QuestionAttachmentUpload } from "../question-attachments-upload";
import { Action, State } from "../reducer";

interface ActiveQuestionProps {
  question: PublicQuestion;
  questions: PublicQuestion[];
  endsAt: Date | null;
  state: State;
  isAnswerPasteDisabled: boolean;
  dispatch: Dispatch<Action>;
  onCopyDetected?: () => void;
  onPasteDetected?: () => void;
}

const QuestionWithDisabledCopy = withDisabledCopy(DSQuestion);

export function ActiveQuestion(props: ActiveQuestionProps) {
  const descriptionRef = useRef<HTMLDivElement>(null);
  const answerRef = useRef<HTMLDivElement>(null);
  const isTablet = useMedia("(min-width: 768px)", true);
  const { query } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const hashedTestId = query.hashedTestId as string;
  const testQuery = useGetTest(jobOpeningSlug, hashedTestId!, !!hashedTestId);
  const { mutate: copyPasteEventsMutation } =
    usePostTestCopyPasteEventsMutation();
  const { question, onCopyDetected, onPasteDetected } = props;
  const testQuestionId = question?.test_question_id || 0;

  const copyListener = useCallback(() => {
    const selection = document.getSelection();
    const text = selection?.toString();

    if (text) {
      copyPasteEventsMutation({
        slug: hashedTestId,
        job_opening_slug: jobOpeningSlug,
        data: {
          copy_paste_events: [
            {
              question_id: testQuestionId,
              text: text,
              event_type: "copy",
              triggered_at: new Date().toISOString(),
            },
          ],
        },
      });

      // Call the copy detected handler if provided
      onCopyDetected?.();
    }
  }, [
    testQuestionId,
    jobOpeningSlug,
    hashedTestId,
    copyPasteEventsMutation,
    onCopyDetected,
  ]);

  const pasteListener = useCallback(
    (event: ClipboardEvent) => {
      const text = event.clipboardData?.getData("text");

      if (text) {
        copyPasteEventsMutation({
          slug: hashedTestId,
          job_opening_slug: jobOpeningSlug,
          data: {
            copy_paste_events: [
              {
                question_id: testQuestionId,
                text: text,
                event_type: "paste",
                triggered_at: new Date().toISOString(),
              },
            ],
          },
        });

        // Call the paste detected handler if provided
        onPasteDetected?.();
      }
    },
    [
      copyPasteEventsMutation,
      hashedTestId,
      jobOpeningSlug,
      testQuestionId,
      onPasteDetected,
    ],
  );

  useEffect(() => {
    const descriptionElement = descriptionRef.current;

    descriptionElement?.addEventListener("copy", copyListener);

    return () => {
      descriptionElement?.removeEventListener("copy", copyListener);
    };
  });

  // Register paste listener
  useEffect(() => {
    const answerElement = answerRef.current;

    answerElement?.addEventListener("paste", pasteListener);

    return () => {
      answerElement?.removeEventListener("paste", pasteListener);
    };
  });
  if (testQuery.isLoading || !testQuery.data) {
    return null;
  }
  const test = testQuery.data.test;
  const number = props.state.activeQuestionIndex + 1;

  return (
    <QuestionWithDisabledCopy
      isSplitMode={test.test_type === "video"}
      key={props.question ? props.question.test_question_id : undefined}
      header={
        isTablet && (
          <DSQuestionTitle>
            <Trans>Question {number}</Trans>
            {props.question
              ? props.question.skill_name
                ? ` — ${props.question.skill_name}`
                : test.test_type === "video"
                  ? " - Video"
                  : ""
              : null}
          </DSQuestionTitle>
        )
      }
      description={
        <Content ref={descriptionRef}>
          {props.question ? (
            <Markdown>{props.question.question}</Markdown>
          ) : null}
        </Content>
      }
      answer={
        <>
          <QuestionAttachmentUpload
            testType={test.test_type}
            question={props.question}
            dispatch={props.dispatch}
          />
          <AnswerWrapper
            ref={answerRef}
            isSplitMode={test.test_type === "video"}
          >
            <ActiveAnswer {...props} />
          </AnswerWrapper>
          <QuestionAttachments
            question={props.question}
            dispatch={props.dispatch}
            attachments={props.state.attachments}
          />
        </>
      }
      footer={<FooterQuestionNavigation {...props} />}
    />
  );
}

//region Styles
const AnswerWrapper = styled.div<{ isSplitMode: boolean }>`
  padding-bottom: 80px;
  z-index: 1;
  position: relative;

  @media (min-width: 768px) {
    padding-bottom: ${(props) => (props.isSplitMode ? "20px" : 0)};
  }

  @media (min-width: 992px) {
    padding-bottom: 0;
  }
`;
//endregion
